import { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Select from 'react-select';

import Loading from "../../../components/Loading";
import Data from '../../../../repository';
import { createStaffSchema } from '../../../../validation/schemas';

import styles from '../../../../styles/Admin.module.scss';

function UpdateStaffForm() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [serverError, setServerError] = useState(null);
	const [services, setServices] = useState([]);
	const [image, setImage] = useState('');
	const [uploadingImage, setUploadingImage] = useState(false);
	const [imageError, setImageError] = useState(null);

	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
		reset
	} = useForm({
		resolver: yupResolver(createStaffSchema),
		defaultValues: {
			firstName: '',
			lastName: '',
			email: '',
			phoneNumber: '',
			title: '',
			bio: '',
			enabled: true,
			Services: []
		}
	});

	useEffect(() => {
		fetchData();
	}, [id]);

	const fetchData = async () => {
		try {
			const [staffRes, servicesRes] = await Promise.all([
				Data.fetchStaffMember(id),
				Data.fetchServices()
			]);

			const staff = staffRes.data;
			setServices(servicesRes.data);

			// Map services to select format
			const selectedServices = staff.Services?.map(s => ({
				value: typeof s === 'string' ? s : s._id,
				label: typeof s === 'string' ? servicesRes.data.find(svc => svc._id === s)?.name : s.name
			})) || [];

			reset({
				firstName: staff.firstName || '',
				lastName: staff.lastName || '',
				email: staff.email || '',
				phoneNumber: staff.phoneNumber || '',
				title: staff.title || '',
				bio: staff.bio || '',
				enabled: staff.enabled,
				Services: selectedServices
			});
			setImage(staff.image || '');
		} catch (error) {
			setServerError('Error fetching data');
			console.error('Error fetching data:', error);
		}
		setLoading(false);
	}

	const onSubmit = async (data) => {
		setSaving(true);
		setServerError(null);

		try {
			await Data.updateStaffMember(id, {
				firstName: data.firstName,
				lastName: data.lastName,
				email: data.email,
				phoneNumber: data.phoneNumber,
				title: data.title,
				bio: data.bio,
				enabled: data.enabled === 'true' || data.enabled === true,
				image: image || ''
			});

			// Update services
			const serviceIds = (data.Services || []).map(s => s.value);
			await Data.setStaffServices(id, serviceIds);

			navigate('/admin/staff');
		} catch (err) {
			setServerError(err.response?.data?.message || 'Failed to update staff member');
			setSaving(false);
		}
	}

	const handleImageUpload = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		setUploadingImage(true);
		setImageError(null);
		try {
			const formData = new FormData();
			formData.append('image', file);
			const response = await Data.uploadImage(formData);
			const imageUrl = response?.data?.data?.Location;
			if (!imageUrl) throw new Error('Image upload did not return a valid URL.');
			setImage(imageUrl);
		} catch (error) {
			setImageError('Failed to upload image. Please try again.');
		} finally {
			setUploadingImage(false);
			event.target.value = '';
		}
	}

	const handleDelete = async () => {
		if (!window.confirm('Are you sure you want to delete this staff member?')) return;

		setSaving(true);
		try {
			await Data.deleteStaffMember(id);
			navigate('/admin/staff');
		} catch (err) {
			setServerError(err.response?.data?.message || 'Failed to delete staff member');
			setSaving(false);
		}
	}

	if (loading) return <Loading button={false} />;

	const serviceOptions = services.map(s => ({ value: s._id, label: s.name }));

	return (
		<form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
			<div>
				<p>First Name: *</p>
				<input
					type="text"
					{...register('firstName')}
					className={errors.firstName ? 'input-error' : ''}
				/>
				{errors.firstName && <span className="form-field-error">{errors.firstName.message}</span>}
			</div>

			<div>
				<p>Last Name: *</p>
				<input
					type="text"
					{...register('lastName')}
					className={errors.lastName ? 'input-error' : ''}
				/>
				{errors.lastName && <span className="form-field-error">{errors.lastName.message}</span>}
			</div>

			<div>
				<p>Title/Role:</p>
				<input
					type="text"
					{...register('title')}
					placeholder="e.g., Practitioner, Stylist"
				/>
			</div>

			<div>
				<p>Email:</p>
				<input
					type="email"
					{...register('email')}
					className={errors.email ? 'input-error' : ''}
				/>
				{errors.email && <span className="form-field-error">{errors.email.message}</span>}
			</div>

			<div>
				<p>Phone Number:</p>
				<input
					type="tel"
					{...register('phoneNumber')}
				/>
			</div>

			<div>
				<p>Bio:</p>
				<textarea
					{...register('bio')}
					rows={4}
					style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
				/>
			</div>

			<div>
				<p>Staff Image:</p>
				<div style={{ display: 'grid', gap: '10px' }}>
					<input
						type="file"
						accept="image/*"
						onChange={handleImageUpload}
						disabled={uploadingImage}
					/>
					{uploadingImage && <span className="form-field-error">Uploading image...</span>}
					{imageError && <span className="form-field-error">{imageError}</span>}
					{image ? (
						<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
							<img
								src={image}
								alt="Staff"
								style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '50%', border: '1px solid #e7e7e7' }}
							/>
							<button
								type="button"
								className="btn"
								onClick={() => setImage('')}
								disabled={uploadingImage}
								style={{ width: 'auto' }}
							>
								Remove Image
							</button>
						</div>
					) : null}
				</div>
			</div>

			<div className={styles.select}>
				<p>Services this staff member can perform:</p>
				<Controller
					name="Services"
					control={control}
					render={({ field }) => (
						<Select
							{...field}
							isMulti
							options={serviceOptions}
							placeholder="Select services..."
						/>
					)}
				/>
			</div>

			<div>
				<p>Status:</p>
				<select {...register('enabled')}>
					<option value={true}>Active</option>
					<option value={false}>Inactive</option>
				</select>
			</div>

			{serverError && <p style={{ color: 'red' }}>{serverError}</p>}

			<div style={{ display: 'flex', gap: '12px' }}>
				<button type="submit" className="btn btnPrimary" disabled={saving}>
					{saving ? 'Saving...' : 'Save Changes'}
				</button>
				<button
					type="button"
					className="btn"
					onClick={handleDelete}
					disabled={saving}
					style={{ background: '#dc3545', color: 'white' }}
				>
					Delete Staff
				</button>
			</div>
		</form>
	)
}

export default UpdateStaffForm;
